import Classnames from "classnames";
import React, { useEffect, useRef, useState } from "react";

import useHtmlProps from "@hooks/use-html-props";

import { getPosition } from "../utils/helpers/portal-position";
import Portal from "./portal";
import style from "./tooltip.styl";

type TooltipPosition =
  | "top-start"
  | "top"
  | "top-end"
  | "left-start"
  | "left"
  | "left-end"
  | "right-start"
  | "right"
  | "right-end"
  | "bottom-start"
  | "bottom"
  | "bottom-end"
  | "in-place";

type TooltipType = "dark" | "light" | "custom";

type TooltipProps = {
  /**
   * Always show tooltip
   *
   * @default false
   */
  alwaysShow?: boolean;
  /**
   * Element to trigger tooltip
   */
  children?: React.ReactNode;
  /**
   * Tooltip className
   */
  className?: string;
  /**
   * Tooltip contents
   */
  contents?: React.ReactNode;
  /**
   * Delay before tooltip appears
   *
   * @default "0.5s"
   */
  delay?: string;
  /**
   * Disable focus on tooltip trigger
   *
   * @default false
   */
  nofocus?: boolean;
  /**
   * Tooltip position
   *
   * @default "top"
   */
  position?: TooltipPosition;
  /**
   * Show tooltip on hover
   *
   * @default true
   */
  showOnHover?: boolean;
  /**
   * Tooltip type
   *
   * @default "dark"
   */
  type?: TooltipType;
  /**
   * Tooltip visibility
   *
   * @default true
   */
  visible?: boolean;
};

const Tooltip = ({
  alwaysShow = false,
  children,
  className: propsClassName,
  contents,
  delay = "0.5s",
  nofocus,
  position = "top",
  showOnHover = true,
  type = "dark",
  visible = true,
  ...props
}: TooltipProps) => {
  const [isOpen, setOpen] = useState(alwaysShow);
  const [positionState, setPosition] = useState<React.CSSProperties>({});
  let childRef = useRef<HTMLElement>(null);
  const contentsRef = useRef<HTMLDivElement>(null);

  const htmlProps = useHtmlProps(props);

  useEffect(() => {
    function updatePosition() {
      if (!visible) return;

      const childElement = childRef && childRef.current;

      if (childElement) {
        const childBounds = childElement.getBoundingClientRect();
        let currentPosition = position;

        // TODO other screen borders
        // prevent tooltip going off bottom of screen
        if (
          position.includes("bottom") &&
          contentsRef.current &&
          childBounds.bottom + contentsRef.current.offsetHeight + 14 >
            window.document.documentElement.offsetHeight
        ) {
          currentPosition = position.replace(
            "bottom",
            "top",
          ) as TooltipPosition;
        }
        // prevent tooltip going off right of screen
        if (
          contentsRef.current &&
          childBounds.left + contentsRef.current.offsetWidth >
            window.document.documentElement.offsetWidth
        ) {
          switch (position) {
            case "top":
            case "top-start":
              currentPosition = "top-end";
              break;
            case "bottom":
            case "bottom-start":
              currentPosition = "bottom-end";
              break;
          }
        }

        const pos = getPosition(childBounds, currentPosition);
        setPosition(pos);
      }
    }

    const onClose = () => setOpen(false);

    if (isOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("wheel", onClose);
    } else {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("wheel", onClose);
    }

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("wheel", onClose);
    };
  }, [isOpen, visible, position]);

  const className = Classnames(style.tooltip, style[type], propsClassName);

  const getChildProps = (existingProps?: any, existingRef?: React.Ref<any>) => {
    if (existingRef) {
      childRef = existingRef as React.MutableRefObject<HTMLElement>;
    }
    return {
      ref: childRef,
      onMouseEnter: () => {
        if (existingProps && typeof existingProps.onMouseEnter === "function") {
          existingProps.onMouseEnter();
        }
        if (!alwaysShow && showOnHover) setOpen(true);
      },
      onMouseLeave: () => {
        if (existingProps && typeof existingProps.onMouseLeave === "function") {
          existingProps.onMouseLeave();
        }
        if (!alwaysShow && showOnHover) setOpen(false);
      },
      onMouseOver: () => {
        if (existingProps && typeof existingProps.onMouseOver === "function") {
          existingProps.onMouseOver();
        }
        if (!alwaysShow && showOnHover) setOpen(true);
      },
      onMouseOut: () => {
        if (existingProps && typeof existingProps.onMouseOut === "function") {
          existingProps.onMouseOut();
        }
        if (!alwaysShow && showOnHover) setOpen(false);
      },
      onFocus: () => {
        if (existingProps && typeof existingProps.onFocus === "function") {
          existingProps.onFocus();
        }
        if (!alwaysShow) setOpen(true);
      },
      onBlur: () => {
        if (existingProps && typeof existingProps.onBlur === "function") {
          existingProps.onBlur();
        }
        if (!alwaysShow) setOpen(false);
      },
    };
  };

  let child = children;

  if (typeof children === "string") {
    child = (
      <span tabIndex={nofocus ? undefined : 0} {...getChildProps()}>
        {children}
      </span>
    );
  } else if (React.isValidElement(children)) {
    child = React.cloneElement(
      children,
      getChildProps(children.props, (children as any).ref),
    );
  }

  if (!contents) {
    return <>{children}</>;
  }

  return (
    <>
      {child}
      {isOpen && visible !== false && (
        <Portal>
          <div
            ref={contentsRef}
            className={className}
            {...htmlProps}
            style={
              {
                ...(htmlProps.style ? htmlProps.style : {}),
                ...positionState,
                "--tooltip-delay": delay,
              } as React.CSSProperties
            }
          >
            {contents}
          </div>
        </Portal>
      )}
    </>
  );
};

export default Tooltip;
